import React, { useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PhoneArrowUpRightIcon,
  ArchiveBoxXMarkIcon,
  DocumentMagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

import logo from "../assets/image/logo.png";
import paso1 from "../assets/image/landing/paso1publica_tu_producto.png";
import paso2 from "../assets/image/landing/paso2comparte_catalogo.png";
import paso3 from "../assets/image/landing/paso3recibe_por_whatsapp.png";
import paso4 from "../assets/image/landing/paso4entrega_o_coordina_pago.png";
import heroIllustration from "../assets/image/landing/banner-removebg-preview.png";

const LandingSeller = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800 antialiased selection:bg-[#1E73BE] selection:text-white">
      {/* --- NAVBAR --- */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100 transition-all shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/">
                <img src={logo} alt="mioprecio.com" className="h-10 w-auto object-contain" />
              </Link>
            </div>

            {/* Desktop Navigation CTA */}
            <div className="hidden md:flex items-center space-x-6">
              <span className="text-gray-500 font-medium hover:text-[#1E73BE] cursor-pointer transition-colors duration-200">
                Iniciar sesión
              </span>
              <button className="bg-[#1E73BE] text-white px-6 py-2.5 rounded-full font-semibold hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                Crear mi tienda
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-500 hover:text-[#1E73BE] focus:outline-none"
              >
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu panel */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-100 px-4 pt-2 pb-4 space-y-2 shadow-inner">
            <button className="block w-full text-left px-4 py-3 text-gray-600 font-medium hover:bg-gray-50 rounded-lg">
              Iniciar sesión
            </button>
            <button className="block w-full text-center bg-[#1E73BE] text-white px-4 py-3 rounded-lg font-semibold shadow-md">
              Crear mi tienda
            </button>
          </div>
        )}
      </nav>

      {/* --- 1. HERO SECTION --- */}
      <section className="pt-28 pb-0 lg:pt-36 overflow-hidden relative bg-gradient-to-b from-[#eef4fb] via-[#f5f8fc] to-white">
        {/* Subtle background accents */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#1E73BE] rounded-full blur-[180px] opacity-[0.07] pointer-events-none translate-x-1/3 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FF7A00] rounded-full blur-[150px] opacity-[0.06] pointer-events-none -translate-x-1/3 translate-y-1/4"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left Column - Text Content */}
            <div className="flex-1 text-center lg:text-left max-w-xl lg:max-w-none">
              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] xl:text-6xl font-extrabold text-gray-900 tracking-tight leading-[1.12] mb-6">
                Empieza{" "}
                <span className="text-[#FF7A00]">a vender</span>
                <br />
                tus productos
                <br />
                online hoy
              </h1>
              <p className="text-lg md:text-xl text-gray-500 mb-8 max-w-lg mx-auto lg:mx-0 font-medium leading-relaxed">
                Crea tu catálogo digital, comparte tu tienda,
                <br className="hidden sm:block" />
                y recibe pedidos directamente por WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start items-center gap-4">
                <button className="w-full sm:w-auto bg-[#FF7A00] text-white px-8 py-3.5 rounded-xl text-base font-bold hover:bg-[#e66d00] transition-all duration-200 shadow-lg shadow-orange-500/20 hover:-translate-y-0.5">
                  Crear mi tienda
                </button>
                <button className="w-full sm:w-auto bg-white text-gray-700 border-2 border-gray-200 px-8 py-3.5 rounded-xl text-base font-bold hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow-md">
                  Ver cómo funciona
                </button>
              </div>
            </div>

            {/* Right Column - Hero Illustration */}
            <div className="flex-1 flex justify-center lg:justify-end relative">
              <img
                src={heroIllustration}
                alt="Vendedor con catálogo digital en teléfono"
                className="w-full max-w-md lg:max-w-lg xl:max-w-xl h-auto object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Wave SVG Separator */}
        <div className="relative w-full mt-8 lg:mt-12">
          <svg
            className="block w-full h-auto"
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M0 60C0 60 240 0 480 30C720 60 960 120 1200 90C1320 75 1380 50 1440 40V120H0V60Z"
              fill="#F9FAFB"
            />
            <path
              d="M0 80C0 80 200 30 440 50C680 70 920 110 1160 80C1300 65 1400 45 1440 35V120H0V80Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* --- 2. PROBLEM SECTION --- */}
      <section className="py-24 bg-gray-50 border-y border-gray-100 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">¿Te pasa esto?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: "Haces lives pero muchos productos no se venden",
                icon: <ExclamationTriangleIcon className="w-7 h-7 text-red-500" />
              },
              { 
                title: "Los clientes preguntan por WhatsApp todo el tiempo",
                icon: <PhoneArrowUpRightIcon className="w-7 h-7 text-red-500" />
              },
              { 
                title: "Tienes productos acumulados sin mostrar",
                icon: <ArchiveBoxXMarkIcon className="w-7 h-7 text-red-500" />
              },
              { 
                title: "Pierdes ventas porque no tienes un catálogo organizado",
                icon: <DocumentMagnifyingGlassIcon className="w-7 h-7 text-red-500" />
              }
            ].map((problem, idx) => (
              <div key={idx} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                  {problem.icon}
                </div>
                <p className="text-gray-800 font-semibold text-lg leading-snug">{problem.title}</p>
              </div>
            ))}
          </div>

          <div className="mt-20 text-center bg-white border border-blue-100 p-8 rounded-3xl shadow-lg max-w-4xl mx-auto relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-[#1E73BE]"></div>
            <p className="text-xl md:text-2xl font-bold text-[#1E73BE]">
              Mioprecio te ayuda a organizar tu catálogo y vender más fácilmente.
            </p>
          </div>
        </div>
      </section>

      {/* --- 3. HOW IT WORKS SECTION --- */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">Cómo funciona</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {[
              { bg: "bg-blue-50", textCol: "text-blue-900", step: 1, title: 'Publica tus productos', desc: 'Sube fotos, precio y descripción en minutos.', img: paso1 },
              { bg: "bg-orange-50", textCol: "text-orange-900", step: 2, title: 'Comparte tu catálogo', desc: 'Envía el link de tu tienda por WhatsApp, TikTok o Instagram.', img: paso2 },
              { bg: "bg-green-50", textCol: "text-green-900", step: 3, title: 'Recibe pedidos', desc: 'Los clientes te contactan directamente para comprar por WhatsApp.', img: paso3 },
              { bg: "bg-purple-50", textCol: "text-purple-900", step: 4, title: 'Entrega o pago', desc: 'Organiza la entrega o coordina el pago con el cliente.', img: paso4 }
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center text-center group">
                <div className={`w-full ${item.bg} rounded-[2rem] p-8 mb-8 transition-transform duration-300 group-hover:-translate-y-3 relative overflow-hidden flex items-center justify-center aspect-square`}>
                  <img src={item.img} alt={item.title} className="w-[85%] h-auto object-contain z-10 transition-transform duration-300 group-hover:scale-105 drop-shadow-sm" />
                </div>
                
                <div className="flex flex-col items-center">
                   <div className="w-10 h-10 bg-[#FF7A00] text-white rounded-xl shadow-md flex items-center justify-center font-bold text-lg mb-5 -rotate-3">
                    {item.step}
                  </div>
                  <h3 className={`text-2xl font-bold ${item.textCol} mb-3`}>{item.title}</h3>
                  <p className="text-gray-600 font-medium text-[1.05rem] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 4. BENEFITS SECTION --- */}
      <section className="py-28 bg-[#1E73BE] text-white relative overflow-hidden">
        {/* Abstract shapes for visual interest */}
        <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-96 h-96 transform rotate-45">
            <path fill="#ffffff" d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.5,90,-16.3,89.1,-0.5C88.2,15.3,83.9,30.6,76,43.8C68.1,57,56.6,68,42.8,75.1C29,82.2,14.5,85.4,0,85.4C-14.5,85.4,-29,82.2,-42.1,74.7C-55.2,67.2,-66.9,55.5,-75.4,41.9C-83.9,28.3,-89.2,14.1,-89.5,-0.2C-89.8,-14.5,-85.1,-29,-76.4,-42.3C-67.7,-55.6,-55.1,-67.7,-41,-74.8C-26.9,-81.9,-13.4,-84,-0.1,-83.8C13.2,-83.6,26.4,-81.2,44.7,-76.4Z" transform="translate(100 100)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mb-20 max-w-4xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
              Todo lo que necesitas para vender online
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 font-medium">Herramientas diseñadas para potenciar tu negocio sin complicaciones.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              "Catálogo online 24/7",
              "Comparte tu tienda fácilmente",
              "Pedidos organizados",
              "Sin conocimientos técnicos",
              "Ideal para vendedores de TikTok y WhatsApp",
              "Control de tus productos"
            ].map((benefit, i) => (
              <div key={i} className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-6 lg:p-8 rounded-[1.5rem] border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex-shrink-0 mt-1 text-[#3CBF4A] bg-white rounded-full p-1 shadow-sm">
                  <CheckIcon className="w-5 h-5 flex-shrink-0" />
                </div>
                <p className="text-white font-semibold text-lg md:text-xl">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 5. PRICING SECTION --- */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">Planes simples y transparentes</h2>
            <p className="text-xl text-gray-600 font-medium">Elige el plan que mejor se adapte a tu tamaño de negocio.</p>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* PLAN 1 */}
            <div className="bg-white rounded-[2.5rem] p-10 shadow-lg border border-gray-100 flex flex-col h-full transform transition hover:-translate-y-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Emprendedor</h3>
              <p className="text-gray-500 mb-6 font-medium">Ideal para dar tus primeros pasos.</p>
              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-6xl font-extrabold text-gray-900">69</span>
                <span className="text-xl font-bold text-gray-500">Bs / mes</span>
              </div>
              
              <ul className="space-y-5 mb-10 flex-grow">
                {[
                  "Hasta 60 productos",
                  "Catálogo online",
                  "Link de tienda personalizado",
                  "Pedidos por WhatsApp",
                  "Soporte básico"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                    <CheckCircleIcon className="w-6 h-6 text-[#1E73BE] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button className="w-full bg-blue-50 text-[#1E73BE] border border-blue-200 py-4 rounded-2xl font-bold text-lg hover:bg-[#1E73BE] hover:text-white transition-colors duration-300">
                Comenzar ahora
              </button>
            </div>

            {/* PLAN 2 */}
            <div className="bg-[#1E73BE] rounded-[2.5rem] p-10 shadow-2xl border-4 border-[#1E73BE] flex flex-col h-full relative transform transition md:-scale-y-100 md:scale-y-100 lg:scale-105 hover:scale-110 z-10">
              <div className="absolute top-0 right-0 transform translate-x-3 -translate-y-4">
                <span className="bg-[#FF7A00] text-white text-sm font-bold uppercase tracking-wider py-1.5 px-4 rounded-full shadow-lg">
                  Más popular
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Negocio</h3>
              <p className="text-blue-200 mb-6 font-medium">Para negocios en pleno crecimiento.</p>
              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-6xl font-extrabold text-white">99</span>
                <span className="text-xl font-bold text-blue-200">Bs / mes</span>
              </div>
              
              <ul className="space-y-5 mb-10 flex-grow">
                {[
                  "Productos ilimitados",
                  "Catálogo online",
                  "Link de tienda personalizado",
                  "Pedidos por WhatsApp",
                  "Soporte prioritario"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-white font-medium text-lg">
                    <CheckCircleIcon className="w-6 h-6 text-[#3CBF4A] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button className="w-full bg-[#FF7A00] text-white py-4 rounded-2xl font-bold text-lg hover:bg-[#e66d00] transition-colors duration-300 shadow-xl shadow-orange-500/30">
                Crear mi tienda
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* --- 6. SOCIAL PROOF SECTION --- */}
      <section className="py-24 bg-white border-t border-gray-100 relative">
         <div className="absolute inset-x-0 h-1/2 bg-gray-50 top-0 pointer-events-none"></div>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">Perfecto para vendedores que usan WhatsApp y TikTok</h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-10 md:p-14 rounded-[3rem] shadow-xl border border-gray-100 text-center relative">
              <span className="absolute top-10 transform -translate-x-1/2 text-8xl text-orange-200 font-serif opacity-50">&ldquo;</span>
              <p className="text-2xl md:text-4xl text-gray-800 font-medium italic leading-relaxed relative z-10 my-8">
                Ahora mis clientes pueden ver todos mis productos sin esperar al live.
              </p>
              <div className="flex items-center justify-center gap-4 mt-8">
                <div className="w-14 h-14 bg-gray-200 rounded-full overflow-hidden">
                   <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="Vendedora" className="w-full h-full object-cover" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900 text-lg">María G.</p>
                  <p className="text-gray-500 text-sm">Vendedora de Ropa en TikTok</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 7. FINAL CTA --- */}
      <section className="py-32 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute -left-40 top-0 w-96 h-96 bg-[#1E73BE] opacity-30 blur-[100px] rounded-full"></div>
          <div className="absolute -right-40 bottom-0 w-96 h-96 bg-[#FF7A00] opacity-30 blur-[100px] rounded-full"></div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-8 tracking-tight">Empieza a vender hoy mismo</h2>
          <p className="text-xl md:text-3xl text-gray-300 mb-12 font-medium max-w-3xl mx-auto">
            Crea tu catálogo online y comparte tu tienda en minutos.
          </p>
          <button className="bg-[#3CBF4A] hover:bg-[#34a840] text-white px-10 py-5 rounded-full text-xl md:text-2xl font-bold transition-all shadow-xl shadow-green-500/20 hover:-translate-y-1 w-full sm:w-auto">
            Crear mi tienda gratis
          </button>
        </div>
      </section>

      {/* --- 8. FOOTER --- */}
      <footer className="bg-white border-t border-gray-100 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            
            <div className="md:col-span-1 border-r-0 md:border-r border-gray-100 pr-8">
              <Link to="/">
                <img src={logo} alt="mioprecio.com" className="h-10 w-auto object-contain mb-6 grayscale hover:grayscale-0 transition-all" />
              </Link>
              <p className="text-gray-500 font-medium">
                La plataforma perfecta para que pequeños negocios vendan online fácilmente.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-6 uppercase tracking-wider text-sm">Plataforma</h4>
              <ul className="space-y-4">
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Cómo funciona</Link></li>
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Planes</Link></li>
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Beneficios</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-6 uppercase tracking-wider text-sm">Soporte</h4>
              <ul className="space-y-4">
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Centro de ayuda</Link></li>
                <li>
                  <a href="#" className="flex items-center gap-2 text-gray-500 hover:text-[#3CBF4A] font-medium transition-colors">
                    Soporte WhatsApp
                  </a>
                </li>
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Contacto</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-6 uppercase tracking-wider text-sm">Legal & Redes</h4>
              <ul className="space-y-4 mb-8">
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Términos y condiciones</Link></li>
                <li><Link to="#" className="text-gray-500 hover:text-[#1E73BE] font-medium transition-colors">Privacidad</Link></li>
              </ul>
              
              <div className="flex gap-4">
                {/* Social Placeholders if you prefer using SVGs instead of standard images */}
                <a href="#" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-[#1E73BE] hover:text-white transition-all">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-black hover:text-white transition-all">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91.01.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93v7.2c0 1.63-.31 3.26-1 4.75-1.11 2.4-3.5 4.18-6.19 4.67-2.71.49-5.59-.11-7.85-1.74-2.11-1.52-3.41-3.95-3.56-6.55-.15-2.61 1.05-5.23 3.09-6.85 2-.45 4.15-.43 6.18.17v4.13c-1.12-.2-2.31-.1-3.35.34-1.51.65-2.45 2.31-2.28 3.95.14 1.41 1.07 2.67 2.39 3.2 1.43.58 3.12.51 4.46-.34 1.25-.8 2.05-2.2 2.1-3.66v-17.4h3.91z"/></svg>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 font-medium">
              &copy; {new Date().getFullYear()} mioprecio.com. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {})(LandingSeller);
